// The member's signing key for the envelope (S17), and in-browser
// verification of everything signed under it.
//
// The key is a WebCrypto ECDSA P-256 pair generated here and kept in this
// browser's IndexedDB as a NON-EXTRACTABLE CryptoKey: the page can ask it to
// sign, but nothing — not this code, not us — can read the private half out.
// Lose the browser and the key goes with it; signing again makes a new one
// and replaces the envelope. Verification mirrors functions/shared/mandate/vc.js
// exactly (ES256 vc+jwt, did:jwk issuers), so what this page says checks out
// is what anyone else would find.

const DB_NAME = 'tou-envelope';
const STORE = 'keys';

const b64url = (bytes) => btoa(String.fromCharCode(...new Uint8Array(bytes))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const fromB64url = (s) => {
  const b = atob(String(s).replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((String(s).length + 3) % 4));
  return Uint8Array.from(b, (c) => c.charCodeAt(0));
};
const utf8 = (s) => new TextEncoder().encode(s);
const json = (part) => JSON.parse(new TextDecoder().decode(fromB64url(part)));

function idb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function idbDo(mode, fn) {
  const db = await idb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const req = fn(tx.objectStore(STORE));
    tx.oncomplete = () => resolve(req && req.result);
    tx.onerror = () => reject(tx.error);
  });
}

export const supported = () => Boolean(window.crypto && window.crypto.subtle && window.indexedDB);

/** This browser's key for this member, made on first use. */
export async function memberKey(uid) {
  const id = `member:${uid}`;
  let stored = null;
  try {
    stored = await idbDo('readonly', (s) => s.get(id));
  } catch (e) {
    stored = null;
  }
  if (stored && stored.privateKey && stored.publicJwk) return stored;
  const pair = await crypto.subtle.generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign', 'verify']);
  const jwk = await crypto.subtle.exportKey('jwk', pair.publicKey);
  const record = { privateKey: pair.privateKey, publicJwk: { crv: 'P-256', kty: 'EC', x: jwk.x, y: jwk.y }, createdAt: Date.now() };
  await idbDo('readwrite', (s) => s.put(record, id));
  return record;
}

export async function hasMemberKey(uid) {
  try {
    const stored = await idbDo('readonly', (s) => s.get(`member:${uid}`));
    return Boolean(stored && stored.publicJwk);
  } catch (e) {
    return false;
  }
}

/** Sign the exact bytes the server drafted. Returns the base64url r||s. */
export async function signInput(key, signingInput) {
  const sig = await crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, key.privateKey, utf8(signingInput));
  return b64url(sig);
}

export const didFor = (jwk) => `did:jwk:${b64url(utf8(JSON.stringify({ crv: 'P-256', kty: 'EC', x: jwk.x, y: jwk.y })))}`;

export async function digestOf(jws) {
  return `sha256:${b64url(await crypto.subtle.digest('SHA-256', utf8(jws)))}`;
}

/**
 * Verify one credential. Never throws: returns {ok, why?, credential?, issuer?}.
 */
export async function verify(jws) {
  try {
    const [h, p, s] = String(jws || '').split('.');
    if (!h || !p || s === undefined) return { ok: false, why: 'not a credential' };
    const header = json(h);
    const credential = json(p);
    if (header.alg !== 'ES256' || header.typ !== 'vc+jwt') return { ok: false, why: 'wrong algorithm' };
    const m = /^did:jwk:([A-Za-z0-9_-]+)$/.exec(credential.issuer || '');
    if (!m || header.kid !== `${credential.issuer}#0`) return { ok: false, why: 'issuer unclear' };
    const jwk = json(m[1]);
    const key = await crypto.subtle.importKey('jwk', { kty: 'EC', crv: 'P-256', x: jwk.x, y: jwk.y }, { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']);
    const ok = await crypto.subtle.verify({ name: 'ECDSA', hash: 'SHA-256' }, key, fromB64url(s), utf8(`${h}.${p}`));
    return ok ? { ok: true, credential, issuer: credential.issuer } : { ok: false, why: 'signature does not verify' };
  } catch (e) {
    return { ok: false, why: 'unreadable' };
  }
}

/**
 * The whole chain for one gift: the member's Intent → our Cart, citing it →
 * our Payment, citing the Cart. Each link is a signature AND a digest.
 */
export async function verifyChain({ intentJws, holderDid, agentDid, cartJws, paymentJws }) {
  const intent = await verify(intentJws);
  const steps = [{ step: 'intent', ok: intent.ok && intent.issuer === holderDid, why: intent.ok ? (intent.issuer === holderDid ? null : 'not your key') : intent.why }];
  if (cartJws) {
    const cart = await verify(cartJws);
    const cites = cart.ok && cart.credential.credentialSubject.intentDigest === await digestOf(intentJws);
    steps.push({ step: 'cart', ok: cart.ok && cart.issuer === agentDid && cites, why: !cart.ok ? cart.why : cart.issuer !== agentDid ? 'not our key' : cites ? null : 'cites a different envelope' });
    if (paymentJws) {
      const pay = await verify(paymentJws);
      const paysCart = pay.ok && pay.credential.credentialSubject.cartDigest === await digestOf(cartJws);
      steps.push({ step: 'payment', ok: pay.ok && pay.issuer === agentDid && paysCart, why: !pay.ok ? pay.why : paysCart ? null : 'pays for a different cart' });
    }
  }
  return { ok: steps.every((x) => x.ok), steps };
}
